'use client'

// sort-imports-ignore
import { FB_PIXEL_ID, OPENAI_PIXEL_ID } from '@dupe/common/constants'
import { flushPendingOpenAiConversions } from '~/src/lib/analytics/openAiConversions'
import { RumErrorBoundary } from '~/src/components/RumErrorBoundary'
import { getCookie } from 'cookies-next'
import { IKContext } from 'imagekitio-react'
import PlausibleProvider from 'next-plausible'
import { ThemeProvider } from 'next-themes'
import { useRouter } from 'next/compat/router'
import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'
import Script from 'next/script'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import type { BootstrapConfig } from 'posthog-js'
import { useEffect, useRef, useState } from 'react'
import { IntercomProvider } from 'react-use-intercom'
import ErrorPage from '~/src/components/ErrorPage'
import { AppProvider } from '~/src/context/AppProvider'
import AuthProvider from '~/src/context/AuthProvider'
import ExtensionModalProvider from '~/src/context/ExtensionModalProvider'
import PromoModalProvider from '~/src/context/PromoModalProvider'
import { ResearchModeProvider } from '~/src/context/ResearchModeProvider'
import { SearchProvider } from '~/src/context/SearchProvider'
import useAuthUser from '~/src/hooks/useAuthUser'
import { adoptFirstSearch } from '~/src/lib/analytics/firstSearchMemory'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import {
  buildExposureFilter,
  buildPageviewProperties,
  trackRouteChangeComplete,
} from '~/src/lib/analytics/routeChangeSideEffects'
import { rememberOpenAiClickId } from '~/src/lib/analytics/openAiConversionRelay'
import type { ExposureFilterRef } from '~/src/lib/analytics/routeChangeSideEffects'
import {
  IMAGEKIT_PUBLIC_KEY,
  IMAGEKIT_URL_ENDPOINT,
  DUPE_FLAGS_COOKIE_KEY,
} from '~/src/lib/constants'
import authenticator from '~/src/lib/imagekit/authenticator'
import {
  dropInterstitialPageEvents,
  dropZarazExceptionEvents,
  isInterstitialPathname,
  POSTHOG_UI_HOST,
  resolveBrowserPostHogApiHost,
} from '~/src/lib/posthogBrowserConfig'
import { PLAUSIBLE_DOMAIN } from '~/src/lib/analytics/redirectPlausiblePageview'
import {
  isSpoofedInAppUserAgent,
  muteAnalyticsForSpoofedInAppBrowser,
} from '~/src/lib/analytics/spoofedInAppBrowser'
import { getVanityUrlPromo, VANITY_URL_PROMOS } from '~/src/lib/vanityUrlPromos'
import { DupeInputStoreProvider } from '~/src/providers/DupeInputStoreProvider'
import { useIntercomActivationStore } from '~/src/stores/useIntercomActivationStore'

// Module scope, not an effect: it must run before the Plausible script (loaded
// afterInteractive) sends its first pageview.
muteAnalyticsForSpoofedInAppBrowser()

// create custom script component with tb_customer_id attribute
interface CustomScriptProps extends React.ComponentProps<'script'> {
  tb_customer_id: string
}

const CustomScript: React.FC<CustomScriptProps> = ({ ...props }) => {
  return <Script {...props} />
}

// Zaraz cutover (docs/features/active/zaraz-migration/): GA4 + Meta Pixel now
// run only via the Cloudflare-edge managed components (see the Zaraz config on
// the dupe.com zone) — no browser-side gtag.js / fbevents.js. This flag is
// kept only as the Strackr "are we in an env with tracking configured" gate;
// Strackr's own affiliate-URL stamping is unrelated to gtag.js and does not
// depend on the removed <GoogleAnalytics> component (see cart-cta-link.ts in
// the extension for why its `_ga`-clientId join is GA-reporting-only, not the
// commission path).
const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID

// Agentation visual-feedback toolbar — local opt-in only. Enable with
// `AGENTATION=true bun dev` (the flag is mapped to the client bundle in
// next.config.ts). Loaded via dynamic import behind a build-time-inlined flag,
// so when AGENTATION is unset the toolbar is tree-shaken out of the bundle and
// never ships to production.
const Agentation =
  process.env.AGENTATION === 'true'
    ? dynamic(() => import('agentation').then((m) => m.Agentation), {
        ssr: false,
      })
    : null

interface AppRouterNavTrackingProps {
  tempUserId?: string
  authUserId?: string
  loadingAuthUser?: boolean
  exposureFilterRef: ExposureFilterRef
}

/**
 * App Router soft-navigation tracking (rendered only when the compat router is
 * null). Mirrors the pages router's `routeChangeComplete` subscription using
 * `usePathname`.
 *
 * Deliberately does NOT use `useSearchParams()`: in the App Router, reading
 * search params from a client component during static generation forces a CSR
 * bailout up to the nearest Suspense boundary — and since AppProviders wraps
 * every page that would destroy SSG/ISR HTML for all app routes. Consequence:
 * query-only soft navigations don't emit a `$pageview` here (pages emits for
 * non-shallow query navs; /reports has none — accepted drift, revisit when a
 * query-driven cluster migrates).
 */
function AppRouterNavTracking({
  tempUserId,
  authUserId,
  loadingAuthUser,
  exposureFilterRef,
}: AppRouterNavTrackingProps): null {
  const pathname = usePathname()

  // Latest auth/user state, readable from the effect without re-firing it when
  // auth resolves — the effect must fire on pathname changes only.
  const latestUserState = useRef({ tempUserId, authUserId, loadingAuthUser })
  latestUserState.current = { tempUserId, authUserId, loadingAuthUser }

  // The initial pageview comes from posthog.init's default capture_pageview
  // (same as the pages router, where routeChangeComplete doesn't fire on first
  // load) — so the effect must NOT fire on mount. Guarded by comparing against
  // the previously seen pathname rather than a first-render boolean, so React
  // StrictMode's double-invoked dev effects can't consume the guard and emit a
  // mount-time pageview.
  const previousPathname = useRef(pathname)

  useEffect(() => {
    if (pathname === previousPathname.current) {
      return
    }

    const { tempUserId, authUserId, loadingAuthUser } = latestUserState.current
    // Don't advance the pointer until we actually track — if auth is still
    // loading, leave previousPathname at its old value so the effect re-fires
    // (for this same pathname) when loadingAuthUser flips to false below.
    if (loadingAuthUser) return

    previousPathname.current = pathname

    trackRouteChangeComplete({
      posthog,
      exposureFilterRef,
      properties: buildPageviewProperties({ tempUserId, authUserId }),
      deviceId: authUserId || tempUserId,
    })
  }, [pathname, exposureFilterRef, loadingAuthUser])

  return null
}

interface AppProvidersProps {
  children: React.ReactNode
  /** Result hash forwarded to AppProvider (pages router: `pageProps.hash`). */
  hash?: string
}

/**
 * The full client provider stack + analytics shell, shared by both routers:
 * `pages/_app.tsx` renders it around `<Component />`, and the App Router root
 * layout renders it around `{children}`. Router differences are bridged with
 * `next/compat/router` (null under app/) and `usePathname`.
 *
 * Order sensitivities (violating any is a runtime break):
 * - IntercomProvider wraps AppProvider (AppProvider calls useIntercom().boot)
 * - AppProvider wraps AuthProvider and below (it holds the app's only
 *   QueryClientProvider and the AppContext consumed by Promo/Extension
 *   providers)
 * - PostHogProvider wraps ExtensionModal/Search providers (useIsFeatureOn)
 * - AuthProvider wraps SearchProvider (useAuthContext().openAuthModal)
 */
export function AppProviders({
  children,
  hash,
}: AppProvidersProps): React.ReactElement {
  // next/compat/router returns the pages NextRouter when mounted under pages/
  // and null under app/ — the branch point for router-specific behavior here.
  const compatRouter = useRouter()
  // SSR-safe under app/; only authoritative when compatRouter is null.
  const appPathname = usePathname()
  // Under pages/, compatRouter.pathname preserves today's SSR-time value (no
  // hydration flash for forcedTheme). The `?? ''` exists for the compat type
  // only (`next/navigation-types/compat/navigation` widens usePathname to
  // `string | null`); under app/ it is never null at runtime, and under pages/
  // the compatRouter branch wins.
  const pathname = (compatRouter ? compatRouter.pathname : appPathname) ?? ''
  // Re-run key for route-scoped effects: asPath under pages (changes on query
  // navs too), pathname under app (query-only navs accepted drift).
  const routeKey = compatRouter ? compatRouter.asPath : appPathname
  // The `/redirect` interstitial's address is a replayable click. Bots were
  // re-firing real shoppers' `/redirect` links minutes after they opened them,
  // so the marketing tags that can read `location.href` don't load there.
  // PostHog and Tinybird stay: both are ours, and PostHog was ruled out as the
  // leak (Brave users, who block the others, still send PostHog events).
  // Strackr stays too: the page's `getTrackingUrl` stamps its GA-reporting tag
  // onto the outbound affiliate URL, and it is self-hosted.
  const thirdPartyTagsAllowed = !isInterstitialPathname(pathname)

  const { tempUserId } = useGetTempUserId()
  const { authUser, loading: loadingAuthUser } = useAuthUser()
  // Tinybird's tracker has no opt-out flag, so the bot fleet is kept out of it
  // by never loading it. Decided after mount (the server has no user agent to
  // go on); until then the tracker waits.
  const [tinybirdTrackerAllowed, setTinybirdTrackerAllowed] = useState(false)
  useEffect(() => {
    setTinybirdTrackerAllowed(!isSpoofedInAppUserAgent(navigator.userAgent))
  }, [])
  // Intercom initializes lazily: nothing loads until a help surface (or the
  // signed-in idle auto-activation in AppProvider) calls activateIntercom().
  const intercomActivated = useIntercomActivationStore((s) => s.activated)
  const [promoData, setPromoData] = useState<
    (typeof VANITY_URL_PROMOS)[number] | null
  >(null)

  // Holds the active `before_send` exposure filter. posthog-js captures
  // `before_send` once at init, so we indirect through this ref and refresh it
  // on route changes (below) to pick up newly-launched experiments without a
  // full page reload.
  const exposureFilterRef = useRef<ExposureFilterRef['current']>(null)

  // Any conversion that never reached OpenAI gets another chance here.
  //
  // A signup has no attempt of its own to fall back on — the account is
  // created once, and the auth effect reports that user as existing from then
  // on. A search nominally retries on the user's next search, but only if
  // they run one; close the tab after a single dupe and it is gone. Both are
  // undercounts, and an undercount is what makes the cost-per-dupe
  // uncomparable against the other three platforms. See `openAiConversions.ts`
  // — a search is only replayed within its own local day.
  useEffect(() => {
    flushPendingOpenAiConversions()
  }, [])

  // posthog init
  useEffect(() => {
    let flagsBootstrap: BootstrapConfig = {}
    try {
      flagsBootstrap = JSON.parse(
        (getCookie(DUPE_FLAGS_COOKIE_KEY) || '{}') as string
      )
    } catch (_error: unknown) {
      // ignore
    }

    // Cost control: `$feature_flag_called` was our #1 event (~36% of all
    // billed events). Drop it for every flag except running-experiment flags
    // (+ deliberate keeps), sourced from PostHog server-side and delivered via
    // the `__dupe_exposure_flags` cookie (set in middleware). Flag values and
    // experiment bucketing are unaffected — only the telemetry event is
    // suppressed. A missing/unknown cookie ⇒ keep_all (safe).
    exposureFilterRef.current = buildExposureFilter()

    // Debug mode logs every `capture()` (event name + property bag) to the
    // console — the fastest way to verify analytics locally without waiting on
    // ingestion. On in local dev (`next dev`), off everywhere else. The reset
    // is explicit because PostHog persists debug mode in localStorage
    // (`ph_debug`), so without forcing it off each load it would stick on in
    // preview/prod for anyone who'd run it in dev on the same origin.
    posthog.debug(process.env.NODE_ENV === 'development')
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host: resolveBrowserPostHogApiHost(),
      ui_host: POSTHOG_UI_HOST,
      bootstrap: flagsBootstrap,
      person_profiles: 'identified_only',
      autocapture: false,
      // Chain: drop Cloudflare Zaraz's third-party `$exception` noise
      // (`/cdn-cgi/zaraz/s.js` — not our code), then drop passive page events
      // on the auto-redirecting `/redirect` interstitial (a redirect hop is not
      // a visitor — see dropInterstitialPageEvents), then apply the
      // exposure allowlist filter. Any stage may drop the event by returning
      // null. Filtering here rather than at each capture site covers both
      // routers and posthog.init's own first-load pageview in one place.
      before_send: (event) => {
        const notZaraz = dropZarazExceptionEvents(event)
        if (!notZaraz) return null
        const kept = dropInterstitialPageEvents(notZaraz)
        if (!kept) return null
        return exposureFilterRef.current
          ? exposureFilterRef.current(kept)
          : kept
      },
      // Session recording is gated by PostHog dashboard rules
      // (Session replay → Recording conditions): URL trigger on /chats/v2/*
      // (the old chats-v2-brain flag condition no longer applies — that flag
      // was deleted on 2026-11-03). Leaving the SDK enabled
      // lets those server-side rules drive when recording starts.
      // Re-enabled 2026-06: the remote config advertises a legacy
      // "Early Access Features" site app (id 137876) that can't be removed
      // from the PostHog UI, and opting out makes posthog-js log a console
      // error on every page. Was false since 2025-07 over suspected
      // rehydration issues via the posthog proxy — revert if those resurface.
      opt_in_site_apps: true,
      capture_heatmaps: true,
      capture_performance: true,
    })

    // Evict the `default_sort_variant` super property. Same class of problem as
    // the `posthog.debug` reset above: super properties live in the browser's
    // own storage and survive indefinitely, so they outlive the code that set
    // them. `rerank-default-sort` registered this on every enrolled visitor and
    // was retired 2026-09-16 along with the hook that used to `unregister` it —
    // without this line every returning visitor from that cohort would keep
    // stamping a stale arm on EVERY event forever, and disagree with the
    // server-side copy (now always null). Safe to call when it was never set.
    // Remove once the pre-2026-09-16 cohort has aged out of its storage.
    posthog.unregister('default_sort_variant')
  }, [])

  // ensure we always set the distinct id to the auth user id or temp user id
  // this will be called again with more user details if we get them via the auth provider, in useAnalytics
  useEffect(() => {
    const distinctId = authUser?.uid || tempUserId
    // A search made before signing in belongs to this account now.
    if (authUser && !authUser.isAnonymous) adoptFirstSearch(authUser.uid)
    const posthogDistinctId = posthog.get_distinct_id()
    if (distinctId && posthogDistinctId !== distinctId) {
      posthog.identify(distinctId)
    }
  }, [authUser, tempUserId])

  // route change handler (pages router only — under app/ the
  // <AppRouterNavTracking /> component rendered below takes over)
  useEffect(() => {
    if (!compatRouter) return
    if (loadingAuthUser) return

    const handleRouteChange = (
      _url: string,
      { shallow }: { shallow?: boolean }
    ) => {
      // Skip tracking for shallow route changes
      if (shallow) {
        return
      }

      // Built per-fire (not captured at subscription time) so an identity
      // change landing between re-subscriptions can't stamp a stale
      // tempUserId/authUserId on the pageview.
      const properties = buildPageviewProperties({
        tempUserId,
        authUserId: authUser?.uid,
      })

      trackRouteChangeComplete({
        posthog,
        exposureFilterRef,
        properties,
        deviceId: authUser?.uid || tempUserId,
      })
    }

    compatRouter.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      compatRouter.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [compatRouter, tempUserId, authUser?.uid, loadingAuthUser])

  // Keep the OpenAI ad-click id from the landing URL, pixel or no pixel.
  // `routeKey` re-runs it after soft navigations.
  useEffect(() => {
    rememberOpenAiClickId()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey])

  // handle vanity url promo — reads ?promo= from window.location inside the
  // effect (client-only, hydration-safe, router-agnostic) instead of
  // router.query; routeKey re-triggers the check after soft navigations.
  useEffect(() => {
    const promo = new URLSearchParams(window.location.search).get('promo')

    if (!promo) return
    const _promoData = getVanityUrlPromo(promo)
    if (_promoData && !promoData) {
      setPromoData(_promoData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey, promoData])

  return (
    <>
      <ThemeProvider
        attribute='class'
        forcedTheme={
          pathname.startsWith('/research') ||
          pathname.startsWith('/blog') ||
          pathname.startsWith('/reports')
            ? 'light'
            : undefined
        }
      >
        <IntercomProvider
          appId={process.env.NEXT_PUBLIC_INTERCOM_APP_ID as string}
          initializeDelay={1000}
          shouldInitialize={
            intercomActivated && !!process.env.NEXT_PUBLIC_VERCEL_ENV
          }
        >
          <AppProvider hash={hash}>
            <RumErrorBoundary
              fallback={() => {
                return <ErrorPage />
              }}
            >
              <PostHogProvider client={posthog}>
                <IKContext
                  urlEndpoint={IMAGEKIT_URL_ENDPOINT}
                  publicKey={IMAGEKIT_PUBLIC_KEY}
                  authenticator={authenticator}
                >
                  <PlausibleProvider
                    domain={PLAUSIBLE_DOMAIN}
                    // A soft navigation to /redirect would otherwise send the
                    // token URL; the page counts itself (redirectPlausiblePageview).
                    exclude='/redirect, /redirect/**'
                    trackOutboundLinks
                    enabled={thirdPartyTagsAllowed}
                  >
                    <AuthProvider>
                      <PromoModalProvider promoData={promoData}>
                        <ExtensionModalProvider disableModal={!!promoData}>
                          <SearchProvider>
                            <ResearchModeProvider>
                              <DupeInputStoreProvider>
                                {GA_MEASUREMENT_ID && (
                                  <>
                                    <Script id='strackr-snippet'>
                                      {`
                                window.strackr=window.strackr||function(){(window.strackr.q = window.strackr.q || []).push(arguments)};
                                window.strackr('create', 'ga', 'G7oLG');
                                window.strackr('setting', 'customposition', 'right');
                                window.strackr('update');
                                `}
                                    </Script>
                                    <Script
                                      async
                                      src='/vendor/strackr/bundle.min.js'
                                    />
                                  </>
                                )}
                                {compatRouter === null && (
                                  <AppRouterNavTracking
                                    tempUserId={tempUserId}
                                    authUserId={authUser?.uid}
                                    loadingAuthUser={loadingAuthUser}
                                    exposureFilterRef={exposureFilterRef}
                                  />
                                )}
                                {children}
                                {Agentation && (
                                  <Agentation endpoint='http://localhost:4747' />
                                )}
                              </DupeInputStoreProvider>
                            </ResearchModeProvider>
                          </SearchProvider>
                        </ExtensionModalProvider>
                      </PromoModalProvider>
                    </AuthProvider>
                  </PlausibleProvider>
                </IKContext>
              </PostHogProvider>
            </RumErrorBoundary>
          </AppProvider>
        </IntercomProvider>
      </ThemeProvider>

      {/* Tinybird */}
      {!!tempUserId && tinybirdTrackerAllowed && (
        <CustomScript
          defer
          src='/vendor/flock.js@1.4.1.js'
          data-host='https://api.us-east.tinybird.co'
          tb_customer_id={tempUserId}
          data-token='p.eyJ1IjogImI0ZTMyMDlmLTVjMGYtNDExOC1iMzk4LWE5MDRiOWE4YTI3YiIsICJpZCI6ICIwZGFlNjA5ZS1kMjRlLTQxZGUtODI1Zi1kMmEwMGQzNmI2MDMiLCAiaG9zdCI6ICJ1c19lYXN0In0.P7bemzO0U4mw8LytlarDleFm9L70MCdFSvrbuYN7v7Y'
        />
      )}
      {/* End Tinybird */}

      {/* Facebook Pixel — stub/loader split: fbq queue + init/PageView
          eager, fbevents.js deferred to idle.

          Load-bearing for the per-search Meta ad conversion `Performed Dupe`
          (DUP-2091, `metaConversions.ts`), for every `sendToFb: true` call
          site in the app (useAnalytics.ts's generic Facebook branch), and for
          the base PageView pixel event below — removing it silently kills all
          of those with no error and no test failure.

          A server-side copy of `Performed Dupe` also reaches Meta today, but
          NOT from our code — it comes from the Stape Conversions API Gateway
          (`capig.stape.gl`), which mirrors this pixel's events. A second
          server-side transport is PREPARED in `metaAdsConversion.ts` (PostHog
          "Meta Ads Conversions") but is NOT delivering: that destination is
          disabled and has never executed. Either way this pixel is the only
          transport that carries `fbp`/`fbc` from the browser, so it is not
          made removable by either of them. */}
      {thirdPartyTagsAllowed && (
        <>
          <Script
            id='fb-pixel-base'
            strategy='afterInteractive'
            dangerouslySetInnerHTML={{
              __html: `
                !function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[]}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '${FB_PIXEL_ID}');
                fbq('track', 'PageView'); // track initial page view
              `,
            }}
          />
          <Script
            id='fb-pixel-loader'
            strategy='lazyOnload'
            src='https://connect.facebook.net/en_US/fbevents.js'
          />
          <noscript>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              alt=''
              height='1'
              width='1'
              style={{ display: 'none' }}
              src={`https://www.facebook.com/tr?id=${FB_PIXEL_ID}&ev=PageView&noscript=1`}
            />
          </noscript>
        </>
      )}
      {/* End Facebook Pixel Scripts */}

      {/* OpenAI (ChatGPT Ads) Pixel — stub/loader split like Meta's above:
          the `oaiq` queue and `init` run eagerly, `oaiq.min.js` is deferred to
          idle. Safe because the stub queues every call on `oaiq.q` and the
          real SDK drains that queue when it loads, so a conversion fired
          before the download lands is still delivered.

          The browser copy of both ChatGPT ad conversions (DUP-2570,
          `openAiConversions.ts`) — the search event and the signup event.
          Removing these scripts silently kills that copy with no error and no
          test failure: every call there just finds `window.oaiq` absent and
          no-ops. The api's server copy (`openAiConversionRelay.ts`) is sent
          independently and would keep firing.

          Deliberately no `debug: true` — the snippet OpenAI's dashboard
          generates carries it, and it logs every event to the browser console.

          Deliberately no `page_viewed` call. OpenAI fires nothing on load (it
          has no automatic PageView), and the two events marketing bids on are
          sent from their own call sites. */}
      {thirdPartyTagsAllowed && (
        <>
          <Script
            id='openai-pixel-base'
            strategy='afterInteractive'
            dangerouslySetInnerHTML={{
              __html: `
                (function(w){
                  if(w.oaiq)return;
                  var q=function(){q.q.push(arguments)};
                  q.q=[];
                  w.oaiq=q;
                })(window);
                oaiq('init', { pixelId: '${OPENAI_PIXEL_ID}' });
              `,
            }}
          />
          <Script
            id='openai-pixel-loader'
            strategy='lazyOnload'
            src='https://bzrcdn.openai.com/sdk/oaiq.min.js'
          />
        </>
      )}
      {/* End OpenAI Pixel Scripts */}

      {/* Podscribe — view-based podcast-ad attribution; the initial 'view'
          fires from the script's own load handler, so deferring the whole
          injector to idle is self-contained. */}
      {thirdPartyTagsAllowed &&
        !loadingAuthUser &&
        (!!authUser?.uid || !!tempUserId) && (
          <Script
            id='podscribe-script'
            strategy='lazyOnload'
            dangerouslySetInnerHTML={{
              __html: `
          (function (w, d) {
              var id = 'podscribe-capture',
              n = 'script';
              var e = d.createElement(n);
              e.id = id;
              e.async = true;
              e.src = 'https://d34r8q7sht0t9k.cloudfront.net/tag.js';
              var s = d.getElementsByTagName(n)[0];
              s.parentNode.insertBefore(e, s);
              e.addEventListener('load', function() {
                w.podscribe('init', { user_id: 'c2b8a3cd-9178-4a43-a255-5a61eba7bab1', advertiser: 'dupe' });
                w.podscribe('view', { device_id: '${authUser?.uid || tempUserId}' }); // track initial page view
              })
            })(window, document);
          `,
            }}
          />
        )}
      {/* End Podscribe */}
    </>
  )
}
